import React, { useState, useEffect } from 'react';
import { appointmentApi, serviceApi } from '../services/api';
import { Card, Button, Table, Alert, Form, Row, Col, Modal } from 'react-bootstrap';

const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    serviceId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    startTime: '',
    notes: ''
  });

  useEffect(() => {
    fetchAppointments();
    fetchServices();
  }, [statusFilter]);

  const fetchServices = async () => {
    try {
      const response = await serviceApi.getAll();
      setServices(response.data.data.services || []);
    } catch (err) {
      console.error('Failed to load services:', err);
    }
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const params = {};

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await appointmentApi.getAll(params);
      setAppointments(response.data.data.appointments);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        await appointmentApi.cancel(appointmentId);
        fetchAppointments();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to cancel appointment');
      }
    }
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);

    try {
      const response = await appointmentApi.create(formData);
      setShowCreateModal(false);
      setFormData({
        serviceId: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        startTime: '',
        notes: ''
      });
      setSuccess('Appointment created successfully!');
      fetchAppointments();
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create appointment');
    } finally {
      setCreating(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div>Loading appointments...</div>;
  }

  return (
    <div className="appointments-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Appointments Management</h2>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Create Appointment
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select value={statusFilter} onChange={handleStatusChange}>
                  <option value="all">All Appointments</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no-show">No Show</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Service</th>
                <th>Date & Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appointments.length > 0 ? (
                appointments.map(appointment => (
                  <tr key={appointment.id || appointment._id}>
                    <td>
                      <strong>{appointment.customerName}</strong>
                      {appointment.customerEmail && (
                        <div className="text-muted">{appointment.customerEmail}</div>
                      )}
                      {appointment.customerPhone && (
                        <div className="text-muted">{appointment.customerPhone}</div>
                      )}
                    </td>
                    <td>{appointment.service?.name || appointment.serviceId?.name || 'N/A'}</td>
                    <td>
                      {new Date(appointment.startTime).toLocaleString()}
                    </td>
                    <td>{appointment.service?.duration || appointment.serviceId?.duration || 0} minutes</td>
                    <td>
                      <span className={`badge bg-${getStatusBadge(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </td>
                    <td>
                      {appointment.status === 'scheduled' && (
                        <>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="me-2"
                            onClick={() => handleCancelAppointment(appointment.id || appointment._id)}
                          >
                            Cancel
                          </Button>
                          <Button variant="outline-primary" size="sm" className="me-2">
                            Reschedule
                          </Button>
                        </>
                      )}
                      <Button variant="outline-info" size="sm">
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">No appointments found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create Appointment Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Appointment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateAppointment}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Form.Group className="mb-3">
              <Form.Label>Service *</Form.Label>
              <Form.Select
                name="serviceId"
                value={formData.serviceId}
                onChange={handleFormChange}
                required
              >
                <option value="">Select a service</option>
                {services.filter(s => s.isActive).map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} ({service.duration} min)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Additional notes or special requests..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={creating}>
              {creating ? 'Creating...' : 'Create Appointment'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

function getStatusBadge(status) {
  switch (status) {
    case 'scheduled': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'danger';
    case 'no-show': return 'warning';
    default: return 'secondary';
  }
}

export default AppointmentsPage;
